import styled from "styled-components";
import ContentLoader from "react-content-loader";
import InfiniteScroll from "react-infinite-scroll-component";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useContext, useEffect, useState } from "react";

import Tabs from "../Tabs";
import TitleWithAction from "../TitleWithAction";
import Textfield from "../../../../components/Textfield/Textfield";
import Button, { ButtonThemes } from "../../../../components/Button/Button";
import IconButton, { IconButtonThemes } from "../../../../components/Button/IconButton";
import { IImage } from "../../../../types/image";
import { TabContentWrapper } from "./tabs.styles";
import { VideoEditorContext } from "../../../../App";
import { MediaType, useMedia } from "../../../../hooks/useMedia";
import { MediaProps, mediaTabs } from "../../../../mocks/humans";
import { getProfile } from "../../../../redux/reducers/profileReducer";
import { BinIcon, CircularProgress, PlusIcon, SendIconOutlined } from "../../../../components/Icons/Icons";

const ACCEPTED_FORMATS = "image/png, image/jpg, image/jpeg";

const MediaTab = () => {
  const profile = useSelector(getProfile);
  const isGuest = profile.roleId === 3;

  const [loadedMedia, setLoadedMedia] = useState<MediaType[]>([]);
  const [loadedAIImage, setLoadedAIImage] = useState<any[]>([]);
  const [activeMedia, setActiveMedia] = useState(mediaTabs[0]);
  const [search, setSearch] = useState("");

  const {
    uploadMediaLoading,
    media,
    mediaLoading,
    mediaHasMore,
    fetchMore,
    images,
    imagesLoading,
    imagesHasMore,
    handleUpload,
    handleUserAssetDelete,
    initiateGenerateImagesSetup,
    setText,
    generatedImages,
    isGeneratedImagesLoading,
    handeGenerateImages,
  } = useMedia({ activeMedia });
  const { handleAddMedia } = useContext(VideoEditorContext);

  const handleImageLoaded = (index: number) => {
    const updatedMedia = [...loadedMedia];
    updatedMedia[index].loaded = true;
    setLoadedMedia(updatedMedia);
  };

  const handleAIImageLoaded = (index: number) => {
    const updatedAIImage = [...loadedAIImage];
    updatedAIImage[index].loaded = true;
    setLoadedAIImage(updatedAIImage);
  };

  const handleActiveMedia = (media: string) => setActiveMedia(media as MediaProps);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value);

  useEffect(() => {
    const newMedia = media?.map((m) => {
      return {
        ...m,
        loaded: false,
      };
    });
    setLoadedMedia(newMedia);
  }, [media]);

  useEffect(() => {
    const newAIImages = images?.map((m) => {
      return {
        ...m,
        loaded: false,
      };
    });
    setLoadedAIImage(newAIImages);
  }, [images]);

  useEffect(() => {
    initiateGenerateImagesSetup();
  }, []);

  const content = {
    [MediaProps.MEDIA]: (
      <MediaWrapper id="scrollableMediaDiv">
        {!isGuest && (
          <UploadWrapper>
            <input type="file" id="media" name="media" accept={ACCEPTED_FORMATS} onChange={handleUpload} />
            {uploadMediaLoading ? <CircularProgress color="#009af7" /> : <PlusIcon />}
          </UploadWrapper>
        )}
        {mediaLoading && !loadedMedia?.length ? (
          <CircularProgressWrapper style={{ height: !isGuest ? "calc(100% - 73px)" : "100%" }}>
            <CircularProgress color="#009af7" />
          </CircularProgressWrapper>
        ) : (
          <InfiniteScroll
            next={fetchMore}
            hasMore={mediaHasMore}
            loader={
              mediaLoading ? (
                <CircularProgressWrapper>
                  <CircularProgress color="#009af7" />
                </CircularProgressWrapper>
              ) : null
            }
            dataLength={media?.length}
            style={{ display: "contents" }}
            scrollableTarget="scrollableMediaDiv"
          >
            {media?.map((media: any, index: number) => (
              <ImageWrapper key={index}>
                {!media.loaded && <Placeholder />}
                <img
                  onClick={(e: React.MouseEvent<HTMLElement>) => {
                    e.stopPropagation();
                    handleAddMedia(media.path);
                  }}
                  src={media.path}
                  onLoad={() => handleImageLoaded(index)}
                  alt=""
                />
                {!!media.userId && (
                  <IconButton
                    icon={<BinIcon />}
                    iconButtonTheme={IconButtonThemes.Secondary}
                    onClick={(e: any) => handleUserAssetDelete(e, media.userAssetID)}
                  />
                )}
              </ImageWrapper>
            ))}
          </InfiniteScroll>
        )}
      </MediaWrapper>
    ),
    [MediaProps.GENERATE]: (
      <GenerateTabWrapper>
        <TextareaWrapper>
          <textarea placeholder="Type your prompt" onChange={(e: any) => setText(e.target.value)} />
          <GenerateImagesButtonWrapper>
            <Button
              buttonTheme={ButtonThemes.Transparent}
              icon={<SendIconOutlined />}
              disabled={isGeneratedImagesLoading}
              onClick={handeGenerateImages}
            />
          </GenerateImagesButtonWrapper>
        </TextareaWrapper>

        <div>
          {isGeneratedImagesLoading ? (
            <GenerateTabContentLoader />
          ) : (
            generatedImages?.map((image: IImage, index: number) => (
              <ImageWrapperLarge key={index}>
                <img
                  src={image.path}
                  onClick={(e: React.MouseEvent<HTMLElement>) => {
                    e.stopPropagation();
                    handleAddMedia(image.path);
                  }}
                  onLoad={() => handleAIImageLoaded(index)}
                />
              </ImageWrapperLarge>
            ))
          )}
        </div>
      </GenerateTabWrapper>
    ),
    [MediaProps.AIIMAGES]: (
      <MediaWrapper id="scrollableMediaDiv">
        {imagesLoading && !loadedAIImage?.length ? (
          <CircularProgressWrapper>
            <CircularProgress color="#009af7" />
          </CircularProgressWrapper>
        ) : (
          <InfiniteScroll
            next={fetchMore}
            hasMore={imagesHasMore}
            loader={
              imagesLoading ? (
                <CircularProgressWrapper>
                  <CircularProgress color="#009af7" />
                </CircularProgressWrapper>
              ) : null
            }
            dataLength={images?.length}
            style={{ display: "contents" }}
            scrollableTarget="scrollableMediaDiv"
          >
            {images.map((image: any, index: number) => (
              <ImageWrapper key={image.visualHistoryID}>
                {!image.loaded && <Placeholder />}
                <img
                  onClick={(e: React.MouseEvent<HTMLElement>) => {
                    e.stopPropagation();
                    handleAddMedia(image.path);
                  }}
                  src={image.path}
                  onLoad={() => handleAIImageLoaded(index)}
                />
              </ImageWrapper>
            ))}
          </InfiniteScroll>
        )}
      </MediaWrapper>
    ),
  };

  return (
    <TabContentWrapper>
      <TitleWithAction title="Media">
        <Tabs data={mediaTabs} active={activeMedia} handleActive={handleActiveMedia} />
        {activeMedia === MediaProps.MEDIA && (
          <Textfield
            value={search}
            placeholder="Search"
            onKeyDown={(event) => {
              if (event.key === "Backspace" || event.key === "Delete") {
                event.stopPropagation();
              }
            }}
            onChange={handleSearchChange}
          />
        )}
        <Content>
          {!isGuest ? (
            content[activeMedia]
          ) : (
            <SignUpLink to="/signup">
              <span>Sign Up</span>
            </SignUpLink>
          )}
        </Content>
      </TitleWithAction>
    </TabContentWrapper>
  );
};

const GenerateTabContentLoader = () => {
  return (
    <ContentLoaderWrapper>
      {Array.from({ length: 2 }).map((_, index) => (
        <ContentLoader
          key={index}
          speed={1}
          width={250}
          height={167}
          viewBox="0 0 250 167"
          backgroundColor="#1d1f23"
          foregroundColor="#4c4d4d"
        >
          <rect x="0" y="0" rx="0" ry="0" width="250" height="167" />
        </ContentLoader>
      ))}
    </ContentLoaderWrapper>
  );
};
const ContentLoaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 16px;
  gap: 16px;
`;

const Content = styled.div`
  position: relative;
  padding-block: 16px;
  min-height: 150px;
  overflow: hidden auto;
  /* width: 272px; */
  flex: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const MediaWrapper = styled.div`
  position: relative;
  overflow: hidden auto;
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-flow: row wrap;
  row-gap: 16px;
  column-gap: 16px;
  margin-bottom: 24px;
  align-content: start;

  .infinite-scroll-component__outerdiv {
    display: contents;
  }

  @media (max-width: 1001px) {
    ::-webkit-scrollbar {
      display: none;
    }
  }

  ::-webkit-scrollbar {
    /* width: 2px; */
    display: none;
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background-color: ${({ theme }) => theme.activeMenu};
  }

  ::-webkit-scrollbar-track {
    margin: 15px 0;
  }
`;

const GenerateTabWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;
const TextareaWrapper = styled.div`
  position: relative;
  width: 100%;
  min-height: 103px;
  background: #1d1f23;
  border: 2px solid #242425;
  & textarea {
    width: 100%;
    height: 100%;
    border: none;
    padding: 12px 16px;
    background: transparent;
    caret-color: white;
    color: white;
  }
`;

const GenerateImagesButtonWrapper = styled.div`
  position: absolute;
  right: 16px;
  bottom: 8px;

  & button {
    svg > path {
      fill: unset;
    }
    :disabled {
      cursor: not-allowed;
    }
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 130px;
  height: 130px;
  overflow: hidden;
  cursor: pointer;

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &:hover > button {
    opacity: 1;
    transition: 0.2s;
  }

  button {
    position: absolute;
    top: 6px;
    right: 6px;
    width: 24px;
    height: 24px;
    box-shadow: none;
    opacity: 0;

    svg {
      width: 12px;
      height: 12px;

      path {
        fill: red;
      }
    }
  }
`;

const ImageWrapperLarge = styled(ImageWrapper)`
  margin-top: 16px;
  width: 250px;
  height: 167px;
  img {
    position: static;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top;
  }
`;

const UploadWrapper = styled.div`
  position: relative;
  width: 130px;
  height: 130px;
  /* border-radius: 16px; */
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed ${({ theme }) => theme.blueDark};

  input {
    position: absolute;
    opacity: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
  }

  svg {
    width: 70px;
    height: 70px;

    path {
      fill: ${({ theme }) => theme.white};
    }
  }
`;

const CircularProgressWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const Placeholder = styled.div`
  width: 130px;
  height: 130px;
  /* border-radius: 12px; */
  background: linear-gradient(-90deg, #e9e9e9, #c4c3c3);
  background-size: 400% 400%;
  animation: gradient 3s ease infinite;

  @keyframes gradient {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }
`;

const SignUpLink = styled(Link)`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    text-decoration: underline;
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
    color: ${({ theme }) => theme.activeMenu};
  }
`;

export default MediaTab;
